import moment from 'moment-timezone';
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import {
  Avatar,
  AvatarRole,
  AvatarSize,
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Card,
  Counter,
  IconName,
  Popover,
  Text,
  TextArea,
  useSnackBar,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import type { Document } from '~/entities/types';
import apiCalls from '~/helpers/apiCalls';

// ============================================================
//     TYPES
// ============================================================
interface Props {
  document: Document;
  onNoteAddSuccess?: () => void;
}

// ============================================================
//     STYLES
// ============================================================
const StyledNoteCard = styled(Box)(({
  flexDirection: 'column',
  gap: 'var(--cl-space-2)',
  backgroundColor: 'var(--cl-gray-200)',
  borderRadius: 'var(--cl-radius-3)',
  paddingBlock: 'var(--cl-space-2)',
  paddingInline: 'var(--cl-space-4)',
}));

// ============================================================
//     COMPONENT
// ============================================================
export const DocReviewNotes: React.FC<Props> = ({ document, onNoteAddSuccess }) => {
  const addSnackbar = useSnackBar();
  const docNotes = document.notes;
  const { typhonDomain } = useContext(UserContext);
  const { notes: { createNote } } = apiCalls;


  const [addNotePopoverOpen, setAddNotePopoverOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');

  const {
    mutateAsync: addNoteToDoc,
    isLoading: isAddingNote,
  } = useMutation({
    mutationFn: async (newNote: string) => {
      // Validate required fields as part of tech debt
      // migration from Daffy backend to Typhon
      if (newNote.length === 0) {
        throw new Error('Note text is required');
      }

      if (!document.document_id) {
        throw new Error('Document ID is required');
      }

      if (!document.document_token?.trim()) {
        throw new Error('Document token is required');
      }

      if (!document.entity_token?.trim()) {
        throw new Error('Entity token is required');
      }

      if (!typhonDomain?.trim()) {
        throw new Error('Typhon domain is required');
      }

      datadogRum.addAction('DocReviewV2Modal', {
        action: 'ADD_DOC_NOTE',
        context: {
          docToken: document.document_token,
        },
      });

      const rowId = typeof document.document_id === 'number'
        ? document.document_id
        : parseInt(String(document.document_id), 10);

      if (isNaN(rowId)) {
        throw new Error('Document ID is required');
      }

      await createNote({
        typhonDomain,
        body: {
          text: newNote,
          table: 'Documents',
          key: 'document_id',
          row_id: rowId,
          token: document.document_token,
          entity_token: document.entity_token,
        },
      });
    },
    onError: (err: any) => {
      addSnackbar({ type: 'danger', title: 'Error Adding Note to Document', body: err?.message });
    },
    onSuccess: () => {
      addSnackbar({ type: 'success', title: 'Document Note Added' });
      setNewNoteText('');
      setAddNotePopoverOpen(false);
      onNoteAddSuccess?.();
    },
  });

  return (
    <Card
      borderless
      collapsible
      collapsed
      compact
      header={(
        <Box justifyContent="space-between" gap={2}>
          <Box justifyContent="flex-start" gap={2}>
            <Text weight={700}>Notes</Text>
            <Counter count={docNotes.length} />
          </Box>
          <span
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Popover
              isOpen={addNotePopoverOpen}
              onOpenChange={setAddNotePopoverOpen}
              side="left"
              align="end"
              trigger={(
                <Button
                  icon={IconName.Add}
                  size={ButtonSize.small}
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  Add Note
                </Button>
              )}
              style={{
                width: 400,
                maxHeight: 300,
              }}
            >
              <Box alignItems="flex-start" justifyContent="flex-start" direction="column" gap={4} style={{ height: '100%', padding: 'var(--cl-space-4)' }}>
                <Text size={4} weight={500} lineHeight={4} as="h3">Add Note</Text>
                <TextArea value={newNoteText} onChange={e => { setNewNoteText(e.target.value); }} />
                <Box justifyContent="flex-end" gap={4}>
                  <Button
                    disabled={isAddingNote}
                    onClick={() => {
                      setAddNotePopoverOpen(false);
                      setNewNoteText('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={isAddingNote || newNoteText.length === 0}
                    variant={ButtonVariant.primary}
                    onClick={() => {
                      addNoteToDoc(newNoteText);
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </Popover>
          </span>
        </Box>
      )}
      contentGap={4}
      headerStyle={{ paddingInline: 0 }}
      contentStyle={{ paddingInline: 0 }}
      separatorStyle={{ margin: 0, maxWidth: '100%', width: '100%' }}
      data-pendo-anchor="document-review-notes"
    >
      <Box direction="column" gap={2}>
        {docNotes.map(note => (
          <StyledNoteCard key={note.note_id}>
            <Box justifyContent="space-between">
              <Avatar
                role={AvatarRole.User}
                name={`${note.agent_first_name} ${note.agent_last_name}`}
                size={AvatarSize.Small}
              />
              <Text size={1} style={{ color: 'var(--cl-text-muted)' }}>
                {moment.tz(note.created_at, 'UTC').tz(moment.tz.guess()).format('LLL z')}
              </Text>
            </Box>
            <Box justifyContent="flex-start">
              {note.text}
            </Box>
          </StyledNoteCard>
        ))}
      </Box>
    </Card>
  );
};
